import { z } from 'zod';
import { RRule } from 'rrule';
import {
  EventRequestStatus,
  EventRequestStatusEnum,
  EventRetailerHostType,
  EventRetailerHostTypeEnum,
  EventType,
  EventTypeEnum,
} from '../event';
import { RRuleOptions, rruleOptionsSchema } from '../rrule';
import { AccountMarket } from '../account';

export type UIMasterEvent = {
  locationIds: string[];
  type: EventType;
  description: string;
  name: string;
  retailerAccountId: string;
  vendorAccountId?: string;
  brandId?: string;
  retailerHostType?: EventRetailerHostType;
  requestStatus?: EventRequestStatus;
  clonedFromId?: string;
  startTime: Date;
  endTime: Date;
  markets?: AccountMarket[];
  quickApprovalCode?: string;
};

export type UIEventSchedule = {
  _id: string;
  rules: RRuleOptions;
  requestStatus: EventRequestStatus;
  timeZone: string;
  masterEvent: UIMasterEvent;
  nextExpansionDate: Date;
  deletedAt?: Date;
  deletedBy?: string;
  stoppedAt?: Date;
  stoppedBy?: string;
};

export const createEventScheduleSchema = z
  .object({
    masterEvent: z.object({
      retailerAccountId: z.string(),
      vendorAccountId: z.string().optional(),
      brandId: z.string().optional(),
      locationIds: z.array(z.string()).min(1),
      retailerHostType: EventRetailerHostTypeEnum.optional(),
      requestStatus: EventRequestStatusEnum.optional(),
      type: EventTypeEnum,
      name: z.string(),
      description: z.string(),
      clonedFromId: z.string().optional(),
      startTime: z.union([z.string().transform((val) => new Date(val)), z.date()]),
      endTime: z.union([z.string().transform((val) => new Date(val)), z.date()]),
    }),
    rules: rruleOptionsSchema,
    timeZone: z.string(),
  })
  .refine(
    (data) => {
      let isValid = true;
      if (data.masterEvent.retailerHostType === 'partner') {
        isValid = isValid && !!data.masterEvent.vendorAccountId && !!data.masterEvent.brandId;
      }
      return isValid;
    },
    {
      message: 'Partner events must include both vendorAccountId and brandId',
      path: ['masterEvent', 'retailerHostType'],
    },
  );

export const RecurringEventChangeScopeEnum = z.enum(['current', 'currentAndFollowing', 'all']);

export const updateEventScheduleSchema = z.object({
  masterEvent: z
    .object({
      retailerAccountId: z.string().optional(),
      vendorAccountId: z.string().optional(),
      brandId: z.string().optional(),
      locationIds: z.array(z.string()).optional(),
      retailerHostType: EventRetailerHostTypeEnum.optional(),
      requestStatus: EventRequestStatusEnum.optional(),
      type: EventTypeEnum.optional(),
      name: z.string().optional(),
      description: z.string().optional(),
      clonedFromId: z.string().optional(),
      startTime: z.union([z.string().transform((val) => new Date(val)), z.date()]).optional(),
      endTime: z.union([z.string().transform((val) => new Date(val)), z.date()]).optional(),
    })
    .optional(),
  timeZone: z.string().optional(),
  // rules: rruleOptionsSchema.optional(), // editing rules not allowed in v1
  updateScope: RecurringEventChangeScopeEnum.optional().default('current'),
  eventId: z.string(),
});

export const deleteEventScheduleSchema = z
  .object({
    deleteScope: RecurringEventChangeScopeEnum.optional().default('current'),
    eventId: z.string().optional(),
  })
  .refine(
    (data) => {
      // If deleteScope is 'current' or 'currentAndFollowing', eventId must be provided
      if (
        (data.deleteScope === 'current' || data.deleteScope === 'currentAndFollowing') &&
        !data.eventId
      ) {
        return false;
      }
      return true;
    },
    {
      message: 'eventId is required when deleteScope is "current" or "currentAndFollowing"',
      path: ['eventId'],
    },
  );

export const declineEventScheduleSchema = z.object({
  reason: z.string().optional(),
  message: z.string().optional(),
});

export const approveEventScheduleSchema = z.object({});

export type CreateEventScheduleRequest = z.infer<typeof createEventScheduleSchema>;
export type UpdateEventScheduleRequest = z.infer<typeof updateEventScheduleSchema>;
export type DeleteEventScheduleRequest = z.infer<typeof deleteEventScheduleSchema>;
export type DeclineEventScheduleRequest = z.infer<typeof declineEventScheduleSchema>;
export type ApproveEventScheduleRequest = z.infer<typeof approveEventScheduleSchema>;

/**
 * Metadata for recurring event navigation
 * Used for navigating between instances of a recurring event series
 */
export interface RecurringEventMetadata {
  scheduleId: string;
  instanceNumber: number;
  totalInstances: number;
  prevInstanceId?: string;
  nextInstanceId?: string;
}

/**
 * Generates a human-readable schedule description from RRule options
 * @param rules - The RRule options for the recurring schedule
 * @returns A formatted string describing the schedule (e.g., "This event occurs every week on Mon, Wed, Fri until Sep 30, 2025")
 */
export function generateScheduleText(rules: RRuleOptions): string {
  try {
    // Convert string dates to Date objects if needed
    const normalizedRules = { ...rules };

    if (normalizedRules.dtstart && typeof normalizedRules.dtstart === 'string') {
      normalizedRules.dtstart = new Date(normalizedRules.dtstart);
    }

    if (normalizedRules.until && typeof normalizedRules.until === 'string') {
      normalizedRules.until = new Date(normalizedRules.until);
    }

    // Use RRule's built-in toText() method for generating human-readable text
    const rrule = new RRule(normalizedRules as any);
    const baseText = rrule.toText();

    // Capitalize first letter
    return baseText.charAt(0).toUpperCase() + baseText.slice(1);
  } catch (error) {
    // Fallback to generic message if schedule text generation fails
    return 'This is an instance of a recurring series';
  }
}
